
import Chip from '@mui/material/Chip'
import AdminPage from 'admin/components/AdminPage'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, map, mergeMap } from 'rxjs/operators'
import { V1 } from 'services/content/statuses/net'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import styles from './CSTagsManager.pcss'

export function CSKeywords() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [keywords, setKeywords] = React.useState<Array<{ id: number, name: string }>>([])

  const fetch$ = React.useRef<Subject<void>>(undefined)
  const create$ = React.useRef<Subject<string>>(undefined)
  const delete$ = React.useRef<Subject<number>>(undefined)

  React.useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current.pipe(
      mergeMap(() => {
        return dispatch(V1.getKeywords()).pipe(catchError(() => {
          dispatch(message('Could not fetch data! Please refresh.'))
          return observableOf([])
        }))
      }))
      .subscribe(response => {
        setKeywords(response)
      })

    fetch$.current.next()

    return () => {
      fetch$.current?.unsubscribe()
    }
  }, [dispatch])

  React.useEffect(() => {
    create$.current = new Subject()
    create$.current.pipe(
      mergeMap((value: string) => {
        return dispatch(V1.addKeyword(value))
          .pipe(
            catchError((e: Error) => {
              dispatch(message('An error occurred: ' + e.message))
              return observableOf({ error: true, value })
            })
          )
      }),
      mergeMap(response => {
        if (response.error) {
          return observableOf(response)
        }
        return dispatch(V1.getKeywords()).pipe(map(response => ({ keywords: response })))
      }))
      .subscribe(response => {
        if (response.error && response.value) {
          setKeywords(current => current.filter(kw => kw.name !== response.value))
          return
        }

        if (response.keywords) {
          setKeywords(response.keywords)
        }
        dispatch(message('Keyword added!'))
      })

    return () => {
      create$.current?.unsubscribe()
    }
  }, [dispatch])

  React.useEffect(() => {
    delete$.current = new Subject()
    delete$.current.pipe(
      mergeMap((id: number) => {
        return dispatch(V1.deleteKeyword(id)).pipe(
          catchError((e: Error) => {
            dispatch(message('An error occurred: ' + e.message))
            return observableOf({ error: true, id })
          })
        )
      }))
      .subscribe(response => {
        const id = parseInt(response.id, 10)
        dispatch(message('Keyword deleted!'))
        setKeywords(current => current.filter(kw => kw.id !== id))
      })

    return () => {
      delete$.current?.unsubscribe()
    }
  }, [dispatch])

  const onDelete = (id: number) => {
    delete$.current?.next(id)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = (e.target as HTMLInputElement).value.toLowerCase().trim()
      if (!value) {
        return
      }

      if (keywords.find(kw => kw.name === value)) {
        dispatch(message('Keyword already exists!'))
        return
      }

      create$.current?.next(value)
      ; (e.target as HTMLInputElement).value = ''
    }
  }

  return (
    <AdminPage title="Post Ideas Keywords">
      <NavLink to="/admin/product/conversation-starters">Back to Post Ideas</NavLink>
      <div className={styles['section-add']}>
        <input type="text" placeholder="Add new keyword" onKeyDown={onKeyDown} className={styles['tag-input']} />
      </div>
      <div className={styles.grid}>
        {keywords.map(kw => (
          <KeywordChip key={kw.id} keyword={kw} onDelete={onDelete} />
        ))}
      </div>
    </AdminPage>
  )
}

const KeywordChip = function (props: { keyword: { id: number, name: string }, onDelete: (id: number) => void }) {
  const onDelete = () => {
    props.onDelete(props.keyword.id)
  }

  return (
    <Chip
      size="small"
      label={props.keyword.name}
      className={styles.chip}
      onDelete={onDelete}
    />
  )
}

export default CSKeywords
